import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MessageCircle, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { useSite } from "../context/SiteContext";
import { IMG } from "../lib/data";
import { submitContact } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import PageHero from "../components/PageHero";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-300 mb-1.5 block">{label}</span>
    <input
      {...props}
      className="w-full bg-[#0A1223] border border-white/12 text-white px-4 py-3 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-colors placeholder:text-slate-600"
    />
  </label>
);

const Contact = () => {
  const { t, lang } = useLanguage();
  const { settings } = useSite();
  const siteContact = settings?.contact || {};
  const [form, setForm] = useState({ full_name: "", company_name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success(t.contactPage.successMsg);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const contacts = [
    { icon: Mail, label: t.contactPage.email, value: siteContact.email || t.footer.emailUs, email: true },
    { icon: MessageCircle, label: t.contactPage.whatsapp, value: siteContact.whatsapp || t.urgent.text, whatsapp: true },
    { icon: MapPin, label: t.contactPage.address, value: (lang === "ar" ? siteContact.address_ar : siteContact.address_en) || t.contactPage.addressValue },
  ];

  const cardCls = "w-full text-start flex items-start gap-4 bg-[#11203A] border border-white/12 p-5 hover:border-[#D4AF37]/40 transition-colors";

  return (
    <div data-testid="contact-page">
      <PageHero label={t.contactPage.label} title={t.contactPage.heading} sub={t.contactPage.sub} image={IMG.cityNight} />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="space-y-5">
              {contacts.map((c, i) =>
                c.whatsapp ? (
                  <a
                    key={i}
                    href={`https://wa.me/${String(siteContact.whatsapp || "966549747549").replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent("whatsapp_click", { location: "contact_page" })}
                    className={cardCls}
                    data-testid={`contact-info-${i}`}
                  >
                    <span className="w-11 h-11 grid place-items-center bg-[#D4AF37] text-[#0A1223] shrink-0">
                      <c.icon size={20} />
                    </span>
                    <div>
                      <p className="section-label !text-slate-400 mb-1">{c.label}</p>
                      <p className="text-slate-100 text-sm">{c.value}</p>
                    </div>
                  </a>
                ) : c.email ? (
                  <a
                    key={i}
                    href={`mailto:${siteContact.email || "rfq@towerlinkgcc.com"}`}
                    onClick={() => trackEvent("email_click", { location: "contact_page" })}
                    className={cardCls}
                    data-testid={`contact-info-${i}`}
                  >
                    <span className="w-11 h-11 grid place-items-center bg-[#D4AF37] text-[#0A1223] shrink-0">
                      <c.icon size={20} />
                    </span>
                    <div>
                      <p className="section-label !text-slate-400 mb-1">{c.label}</p>
                      <p className="text-[#D4AF37] text-sm font-semibold">{c.value}</p>
                    </div>
                  </a>
                ) : (
                  <div key={i} className="flex items-start gap-4 bg-[#11203A] border border-white/12 p-5" data-testid={`contact-info-${i}`}>
                    <span className="w-11 h-11 grid place-items-center bg-[#D4AF37] text-[#0A1223] shrink-0">
                      <c.icon size={20} />
                    </span>
                    <div>
                      <p className="section-label !text-slate-400 mb-1">{c.label}</p>
                      <p className="text-slate-100 text-sm">{c.value}</p>
                    </div>
                  </div>
                )
              )}
            </div>
            <div className="mt-6 border border-white/12 overflow-hidden h-64">
              <iframe
                title="map"
                src="https://www.google.com/maps?q=Saudi%20Arabia&z=5&output=embed"
                className="w-full h-full grayscale contrast-125 opacity-90"
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="glass p-10 text-center h-full grid place-items-center"
                data-testid="contact-success"
              >
                <div>
                  <CheckCircle2 size={56} className="text-[#D4AF37] mx-auto mb-5" />
                  <h3 className="font-heading text-2xl font-bold text-white">{t.contactPage.formHeading}</h3>
                  <p className="mt-3 text-slate-300 max-w-md mx-auto">{t.contactPage.successMsg}</p>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="glass p-7 md:p-9" data-testid="contact-form">
                <h3 className="font-heading text-2xl font-bold text-white mb-6">{t.contactPage.formHeading}</h3>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label={t.rfqPage.fullName} value={form.full_name} onChange={set("full_name")} data-testid="contact-name" />
                  <Field label={t.rfqPage.company} value={form.company_name} onChange={set("company_name")} data-testid="contact-company" />
                  <Field label={t.contactPage.email} type="email" value={form.email} onChange={set("email")} data-testid="contact-email" />
                  <Field label={t.contactPage.phone} value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
                </div>
                <div className="mt-5">
                  <Field label={t.contactPage.subject} value={form.subject} onChange={set("subject")} data-testid="contact-subject" />
                </div>
                <label className="block mt-5">
                  <span className="text-sm font-medium text-slate-300 mb-1.5 block">{t.contactPage.message}</span>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    data-testid="contact-message"
                    className="w-full bg-[#0A1223] border border-white/12 text-white px-4 py-3 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-colors placeholder:text-slate-600"
                  />
                </label>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit"
                  className="btn-gold mt-6 inline-flex items-center gap-2 px-7 py-3.5 disabled:opacity-50"
                >
                  {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  {t.common.sendMessage}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
